import {
  ChangeEvent,
  FocusEventHandler,
  ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { colors } from 'ui/theme/colors';
import noop from 'utils/noop';

import { Positioner, StyledNegativeFill } from './../InputStyles';
import {
  CharacterCount,
  StyledTextArea,
  StyledTextAreaGrowWrap,
  StyledTextAreaWrapper,
} from './TextArea.styles';
import { TextAreaProps } from './TextArea.types';

export const TEXTAREA_TEST_ID = 'textarea-text';
export const CHARACTER_COUNT_TEST_ID = 'character-count';

export const TextArea = forwardRef(
  (
    {
      Prefix = null,
      Suffix = null,
      alwaysShowSuffix = false,
      autoFocus = false,
      autoGrowMinHeight = 0,
      className,
      errorTheme = {
        errorColor: colors.red100,
        Icon: StyledNegativeFill,
      },
      hasError = false,
      hasIcon = true,
      hasWrapperStyling = true,
      id = '',
      inputRef = null,
      isCharacterCountShown = false,
      isCharacterCountTemporarilyVisible = false,
      isDisabled = false,
      isReadOnly = false,
      isRequired = false,
      maxLength,
      onBlur = noop,
      onChange = noop,
      onFocus = noop,
      placeholder = '',
      resize = 'none',
      showFocused = false,
      textAreaWrapperRef = null,
      value = '',
      ...props
    }: TextAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const showSuffix = (!hasError || alwaysShowSuffix) && Suffix;
    const isEditable = !isReadOnly && !isDisabled;
    const areaGrowRef = useRef<HTMLDivElement>(null);
    const inputEl = inputRef || ref;
    const { errorColor, Icon } = errorTheme;

    const onContentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e?.target?.value);
    };

    const handleFocus: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length);
      setIsFocused(true);
      onFocus(e);
    };

    const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      setIsFocused(false);
      onBlur(e);
    };

    useEffect(() => {
      if (autoGrowMinHeight && areaGrowRef.current) {
        areaGrowRef.current.dataset.replicatedValue = value;
      }
    }, [autoGrowMinHeight, value]);

    const renderTextArea = () => (
      <StyledTextArea
        aria-required={isRequired}
        autoFocus={autoFocus}
        autoGrowMinHeight={autoGrowMinHeight}
        data-test={TEXTAREA_TEST_ID}
        disabled={isDisabled}
        errorTheme={errorTheme}
        hasError={hasError}
        id={id}
        maxLength={maxLength}
        onBlur={handleBlur}
        onChange={onContentChange}
        onFocus={handleFocus}
        placeholder={placeholder}
        readOnly={isReadOnly}
        ref={inputEl}
        required={isRequired}
        resize={resize}
        tabIndex={!isEditable ? -1 : 0} // ignore tabbing when in read-only mode
        value={value ?? ''}
        {...props}
      />
    );

    const CharacterLimit = () => {
      if (!maxLength || !isCharacterCountShown) return null;
      if (isCharacterCountTemporarilyVisible && (!isFocused || maxLength - value.length > 10))
        return null;

      return (
        <CharacterCount data-test={CHARACTER_COUNT_TEST_ID}>
          {value?.length || 0}/{maxLength}
        </CharacterCount>
      );
    };

    return (
      <>
        <StyledTextAreaWrapper
          $hasWrapperStyling={hasWrapperStyling}
          className={className}
          data-error={hasError}
          data-show-focused={showFocused ? 'true' : 'false'}
          data-test={props['data-test'] ? `${props['data-test']}-wrapper` : 'textarea-wrapper'}
          errorTheme={errorTheme}
          hasError={hasError}
          isReadOnly={!isEditable}
          ref={textAreaWrapperRef}
        >
          {Prefix && (
            <Positioner
              className="textarea-prefix"
              color={colors.textPrimary}
              data-test="textarea-prefix"
            >
              {Prefix}
            </Positioner>
          )}

          {autoGrowMinHeight ? (
            <StyledTextAreaGrowWrap autoGrowMinHeight={autoGrowMinHeight} ref={areaGrowRef}>
              {renderTextArea()}
            </StyledTextAreaGrowWrap>
          ) : (
            renderTextArea()
          )}

          {showSuffix && (
            <Positioner color={colors.grey60} data-test="textarea-suffix">
              {Suffix}
            </Positioner>
          )}

          {hasError && hasIcon && (
            <Positioner data-test="error-icon">
              <Icon color={errorColor} />
            </Positioner>
          )}
        </StyledTextAreaWrapper>

        <CharacterLimit />
      </>
    );
  },
);
